import { Injectable } from '@angular/core';
import { LocalizationService } from '@abp/ng.core';
import { BehaviorSubject } from 'rxjs';

export type MetroToastSeverity = 'neutral' | 'success' | 'info' | 'warning' | 'error';

export interface MetroToastOptions {
  life?: number;
  sticky?: boolean;
  closable?: boolean;
  tapToDismiss?: boolean;
  messageLocalizationParams?: string[];
  titleLocalizationParams?: string[];
}

export interface MetroToast {
  id: number;
  message: string;
  title?: string;
  severity: MetroToastSeverity;
  options: MetroToastOptions;
}

@Injectable({ providedIn: 'root' })
export class MetroToastService {
  private toastsSubject = new BehaviorSubject<MetroToast[]>([]);
  readonly toasts$ = this.toastsSubject.asObservable();
  private lastId = 0;

  constructor(private localization: LocalizationService) {}

  info(message: any, title?: any, options?: Partial<MetroToastOptions>) {
    return this.show(message, title, 'info', options);
  }

  success(message: any, title?: any, options?: Partial<MetroToastOptions>) {
    return this.show(message, title, 'success', options);
  }

  warn(message: any, title?: any, options?: Partial<MetroToastOptions>) {
    return this.show(message, title, 'warning', options);
  }

  error(message: any, title?: any, options?: Partial<MetroToastOptions>) {
    return this.show(message, title, 'error', options);
  }

  show(message: any, title?: any, severity: MetroToastSeverity = 'neutral', options: Partial<MetroToastOptions> = {}) {
    const id = ++this.lastId;
    const toast: MetroToast = {
      id,
      message: this.translate(message, options.messageLocalizationParams),
      title: title !== undefined ? this.translate(title, options.titleLocalizationParams) : undefined,
      severity,
      options: {
        life: options.life ?? 5000,
        sticky: options.sticky ?? false,
        closable: options.closable ?? true,
        tapToDismiss: options.tapToDismiss ?? true,
        messageLocalizationParams: options.messageLocalizationParams,
        titleLocalizationParams: options.titleLocalizationParams
      }
    };

    const nextToasts = [...this.toastsSubject.getValue(), toast];
    this.toastsSubject.next(nextToasts);

    if (!toast.options.sticky) {
      setTimeout(() => this.remove(id), toast.options.life);
    }

    return id;
  }

  remove = (id: number) => {
    const nextToasts = this.toastsSubject.getValue().filter(t => t.id !== id);
    this.toastsSubject.next(nextToasts);
  };

  clear(containerKey?: string) {
    // containerKey kept for compatibility; not used in this implementation
    this.toastsSubject.next([]);
  }

  private translate(text: any, params?: string[]): string {
    if (typeof text !== 'string') {
      return text !== undefined && text !== null ? String(text) : '';
    }

    const shouldLocalize = text.startsWith('::') || text.includes('::') || text.includes('Abp');

    if (shouldLocalize) {
      const localized = this.localization.instant(text, ...(params ?? [])) as string;
      if (localized && localized !== text) return localized;
    }

    if (params && params.length) {
      const localized = this.localization.instant(text, ...params) as string;
      if (localized && localized !== text) return localized;
    }

    return text;
  }
}
